import React, { useEffect, useState, useRef } from 'react';
import { Chart, registerables } from 'chart.js';
import 'chartjs-plugin-annotation';
import { useSelector } from 'react-redux';
import Modal from 'react-modal';
import {
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  useTheme
} from '@mui/material';
import { useAuthToken } from "../TokenContext";
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';

const CardEpicstalent = ({initialPodIds, selectedSprint}) => {
  const [epicsData, setEpicsData] = useState(null);
  const [openEpics, setOpenEpics] = useState([]);
  const [completedEpics, setCompletedEpics] = useState([]);
  const [selectedReporter, setSelectedReporter] = useState("");
  const [tableData, setTableData] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const chartRefs = useRef({});
  const token = useAuthToken();
  const intl = useIntl();
  const { user } = useSelector((state) => state.auth);
  const theme = useTheme();

  // Initialize Chart.js when component mounts
  useEffect(() => {
    Chart.register(...registerables);
    fetchEpicsData();
  }, [token, initialPodIds, selectedSprint]);

  // Fetch epics data from API
  const fetchEpicsData = async () => {
    if (!token || !user?.data?.[0]) {
      setIsLoading(false);
      return;
    }

    try {
      let url;
      let payload = {};

      // Determine the endpoint and payload based on user type
      if (user.data[0].type === "stakeholder") {
        url = process.env.REACT_APP_STAKEHOLDER_EPICSCOUNTMETRICSSTAKEHOLDER;
        payload = {
          org_code: user.data[0].client_code,
          pod_id: initialPodIds || [],
          sprint_name: selectedSprint || ''
        };
      } else if (user.data[0].type === "Candidate" || user.data[0].type === "talent") {
        url = process.env.REACT_APP_TALENT_EPICSCOUNTMETRICSTALENT;
        payload = {
          c_id: user.data[0].c_id ,
          pod_id: initialPodIds || [],
          sprint_name: selectedSprint || ''
        };
      } else {
        // Default to manager
        url = process.env.REACT_APP_CLIENT_EPCCOUNTMETRICSMANAGER;
        payload = {
          manager_id: user.data[0].emp_id,
          pod_id: initialPodIds || [],
          sprint_name: selectedSprint || ''
        };
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      
      if (data && data.body) {
        const responseData = data.body;
        
        // Ensure we're working with proper data structures
        const openEpicsDetails = Array.isArray(responseData.open_epics_details) 
          ? responseData.open_epics_details 
          : [];
          
        const completedEpicsDetails = Array.isArray(responseData.completed_epics_details) 
          ? responseData.completed_epics_details 
          : [];
          
        setEpicsData(responseData);
        setOpenEpics(openEpicsDetails);
        setCompletedEpics(completedEpicsDetails);
        setTableData([...openEpicsDetails, ...completedEpicsDetails]);
        
        // Generate chart once data is loaded
        const chartData = [
          responseData.open_epics || 0, 
          responseData.completed_epics || 0
        ];
        displayChart('epicsChart', 'Epics', chartData);
      } else {
        setEpicsData({ open_epics: 0, completed_epics: 0 });
        setOpenEpics([]);
        setCompletedEpics([]);
        setTableData([]);
        displayChart('epicsChart', 'Epics', [0, 0]);
      }
    } catch (error) {
      console.error('Error fetching epics data:', error);
      setEpicsData({ open_epics: 0, completed_epics: 0 });
      setOpenEpics([]);
      setCompletedEpics([]);
      setTableData([]);
      displayChart('epicsChart', 'Epics', [0, 0]);
    } finally {
      setIsLoading(false);
    }
  };

  // Generate pie chart data
  const generatePieData = () => {
    const totalOpenEpics = epicsData?.open_epics || 0;
    const totalCompletedEpics = epicsData?.completed_epics || 0;
    return [totalOpenEpics, totalCompletedEpics];
  };

  // Update chart data when epics data changes
  useEffect(() => {
    if (epicsData) {
      const chartData = generatePieData();
      displayChart('epicsChart', 'Epics', chartData);
    }
  }, [epicsData, selectedReporter]);

  // Function to display pie chart - matched to the Detailstoriesmetricstalent component
  const displayChart = (chartId, chartLabel, chartData) => {
    const ctx = document.getElementById(chartId);
    
    if (!ctx) {
      return;
    }

    // Destroy existing chart if it exists
    if (chartRefs.current[chartId]) {
      chartRefs.current[chartId].destroy();
    }

    chartRefs.current[chartId] = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: ['Open Epics', 'Completed Epics'],
        datasets: [
          {
            label: chartLabel,
            data: chartData,
            backgroundColor: ['lightgreen', 'red'], // Matched colors from the first component
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          annotation: {},
          legend: {
            display: false,
          },
        },
        layout: {
          padding: {
            top: 10,
            bottom: 10,
            left: 10,
            right: 10,
          },
        },
        tooltips: {
          enabled: true,
          mode: 'single',
          callbacks: {
            label: function (tooltipItem, data) {
              const dataIndex = tooltipItem.dataIndex;
              const datasetIndex = tooltipItem.datasetIndex;
              const value = data.datasets[datasetIndex].data[dataIndex];
              return value;
            },
          },
        },
        onClick: () => {
          openModal();
        },
      },
    });
  };

  // Handle pie chart click - mirroring the functionality from Detailstoriesmetricstalent
  const handlePieChartClick = (event) => {
    openModal();
  };

  // Handle reporter selection
  const handleReporterChange = (e) => {
    setSelectedReporter(e.target.value);
  };

  // Functions to open/close modal
  const openModal = () => {
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
  };

  return (
    <div className="chart-container">
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <h2 className="chart-heading">{intl.formatMessage({ id: 'epics' }, { defaultMessage: 'Epics' })}</h2>
        <br />
        <br />
        {/* <select   
          value={selectedReporter}
          onChange={(e) => setSelectedReporter(e.target.value)}
          style={{ 
            width: '12rem', 
            color: theme.palette.mode === 'dark' ? 'white' : 'black',      
            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : 'white',
          }}
        >
          <option value="">{intl.formatMessage({id: 'allReporters'}, { defaultMessage: 'All Reporters' })}</option>
        
        </select> */}
        <br/>
        <br/>
        <div className="chart-wrapper" style={{ width: "145px", height: "145px", margin: "auto" }}>
          {!isLoading ? (
            epicsData && epicsData.open_epics === 0 && epicsData.completed_epics === 0 ? (
              <p>{intl.formatMessage({ id: 'noDataAvailable' }, { defaultMessage: 'No data available' })}</p>
            ) : (
              <canvas
                key="epicsChart"
                id="epicsChart"
                onClick={(event) => handlePieChartClick(event)}
              ></canvas>
            )
          ) : (
            <p>{intl.formatMessage({ id: 'noDataAvailable' }, { defaultMessage: 'No data available' })}</p>
          )}
        </div>
      </div>
      
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Epics Summary Modal"
        style={{
          content: {
            width: '50%',
            maxWidth: '800px',
            margin: 'auto',
            marginTop: '5rem',
          },
        }}
      >
        <TableContainer component={Paper}>
          <Table component={Paper} sx={{ maxWidth: 900, fontFamily: 'Poppins,Medium', borderRadius: '10px' }} aria-label="simple table">
            <TableHead
              sx={{
                backgroundColor: '#00B4D8',
                '& td, & th': {
                  fontFamily: 'Poppins,Medium',
                  fontSize: '15px',
                  color: 'white',
                  fontWeight: '600',
                },
              }}
            >
              <TableRow>
                <TableCell align="right">{intl.formatMessage({ id: 'epicKey', defaultMessage: 'Epic Key' })}</TableCell>
                <TableCell align="right">{intl.formatMessage({ id: 'epicSummary', defaultMessage: 'Epic Summary' })}</TableCell>
                <TableCell align="right">{intl.formatMessage({ id: 'status', defaultMessage: 'Status' })}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableData.map((epic, index) => (
                <TableRow
                  key={index}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0, fontFamily: 'Poppins,Medium', fontSize: '15px' },
                    '&:nth-child(even)': {
                      backgroundColor: '#90E0EF',
                    },
                    '&:nth-child(odd)': {
                      backgroundColor: '#caf0f8',
                    },
                    '& td, & th': {
                      fontFamily: 'Poppins,Medium',
                      fontSize: '15px',
                      color: 'black',
                    },
                  }}
                >
                  <TableCell align="right">{epic.key}</TableCell>
                  <TableCell align="right">{epic.summary}</TableCell>
                  <TableCell align="right">{epic.status}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <button onClick={closeModal}>Close</button>
      </Modal>
    </div>
  );
};

CardEpicstalent.propTypes = {
  initialPodIds: PropTypes.array.isRequired,
  selectedSprint: PropTypes.string
};

export default CardEpicstalent;